import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { OCCASIONS } from "@/lib/content";
import { STEPS, zipStatus, validateDetails } from "@/lib/booking";
import { api, errorMessage } from "@/lib/api";
import { toggleAddon as toggleAddonFn, usd, DEPOSIT, priceSummary } from "@/lib/addons";

const INITIAL = {
  zip: "", homeType: "", size: "", stove: "", reason: "",
  date: "", dateLabel: "", window: "", addons: [],
  name: "", email: "", phone: "", address: "", apt: "", access: "", website: "",
};

const stepComplete = (step, d) => {
  if (step === 0) return /^\d{5}$/.test(d.zip) && Boolean(d.homeType && d.size && d.stove);
  if (step === 2) return Boolean(d.date && d.window);
  if (step === 3) return Object.keys(validateDetails(d)).length === 0;
  return true;
};

const toPayload = (d) => ({
  zip: d.zip, home_type: d.homeType, size: d.size, stove: d.stove, reason: d.reason,
  date: d.date, window: d.window, name: d.name, email: d.email.trim(), phone: d.phone,
  address: d.address, apt: d.apt, access: d.access, website: d.website, addons: d.addons,
});

const useRequest = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn) => {
    setSubmitting(true); setError("");
    try { return await fn(); } catch (e) { setError(errorMessage(e)); return null; } finally { setSubmitting(false); }
  };
  return { submitting, error, setError, run };
};

export const useBooking = () => {
  const [params] = useSearchParams();
  const prefill = params.get("reason");
  const [data, setData] = useState(() => ({ ...INITIAL, reason: OCCASIONS.some((o) => o.id === prefill) ? prefill : "" }));
  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState({});
  const [result, setResult] = useState(null);
  const [waitlisted, setWaitlisted] = useState(false);
  const [addonNote, setAddonNote] = useState("");
  const { submitting, error, setError, run } = useRequest();

  const outside = zipStatus(data.zip) === "outside";
  const last = step === STEPS.length - 1;
  const errors = validateDetails(data);
  const shownErrors = Object.fromEntries(Object.entries(errors).filter(([k]) => touched[k] || touched.__all));
  const price = priceSummary(data.addons);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));
  const back = () => { setError(""); setStep((s) => s - 1); };

  const toggleAddon = (id) => {
    const { selected, note } = toggleAddonFn(data.addons, id);
    set({ addons: selected });
    setAddonNote(note);
  };

  const submit = async () => {
    setTouched({ __all: true });
    if (Object.keys(errors).length) return;
    const r = await run(async () => {
      const created = (await api.post("/bookings", toPayload(data))).data;
      if (!created.payment_required) return created;
      const session = (await api.post("/checkout/session", { booking_id: created.id, origin: window.location.origin })).data;
      window.location.assign(session.url);
      return { ...created, redirecting: true };
    });
    if (r && !r.redirecting) setResult(r);
  };

  const next = () => {
    if (last) return submit();
    if (step === 3 && !stepComplete(3, data)) { setTouched({ __all: true }); return; }
    setError("");
    setStep((s) => s + 1);
  };

  const joinWaitlist = async (w) => {
    const r = await run(() => api.post("/waitlist", { ...w, zip: data.zip, website: data.website }));
    if (r) setWaitlisted(true);
  };

  const payLabel = price.addonTotal ? `Pay ${usd(price.dueToday)} and reserve` : `Reserve assessment — ${usd(DEPOSIT)}`;

  return {
    data, set, step, outside, back, next, submitting, error, result, waitlisted, joinWaitlist, toggleAddon, addonNote, price,
    shownErrors, touch, canProceed: stepComplete(step, data),
    continueLabel: last ? payLabel : "Continue",
  };
};
